/**
 * Tax Purpose Adjustments — Inventory (Підпорція 3.1b, 2026-05-24).
 *
 * Зміна податкового призначення запасів. Per-item проводки Дт 643 — Кт 6442.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { tpInventory, type TPInventory } from '@/api/vatAdjustments';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const fmt = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2 });

export default function TPInventoryPage() {
  const navigate = useNavigate();
  const list = useQuery({
    queryKey: ['tp-inventory'],
    queryFn: () => tpInventory.list({ limit: 200 }),
  });

  const cols = useMemo<ColumnDef<TPInventory>[]>(() => [
    { key: 'document_number', header: '№', width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
    {
      key: 'document_date', header: 'Дата', width: 170,
      sortValue: (r) => r.document_date ?? '',
      cell: (r) => r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '',
    },
    { key: 'adjustment_reason', header: 'Причина', sortValue: (r) => r.adjustment_reason ?? '', cell: (r) => r.adjustment_reason },
    {
      key: 'total_vat_to_compensate', header: 'ПДВ компенсація', width: 160, align: 'right',
      sortValue: (r) => Number(r.total_vat_to_compensate), cell: (r) => fmt(r.total_vat_to_compensate),
    },
    {
      key: 'posted', header: 'Проведено', width: 120, align: 'center',
      sortValue: (r) => r.posted ? 1 : 0, cell: (r) => r.posted ? '✓ так' : 'ні',
    },
  ], []);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Зміна податкового призначення (запаси)</h2>
        <button
          className="primary"
          onClick={() => navigate('/taxation/tax-purpose-adj-inventory/new')}
        >
          + Новий
        </button>
      </div>
      <DataTable<TPInventory>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/taxation/tax-purpose-adj-inventory/${r.id}`)}
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
